/**
 * ConnectionStatus Component
 * Displays real-time connection status with auto-dismiss and retry functionality.
 *
 * Props:
 *   minimal     - Show only a small dot indicator (default: false)
 *   showWhenConnected - Flash a "connected" message briefly (default: false)
 *   context     - 'global' (auto-dismiss errors) or 'webinar' (persistent errors)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSocket, CONNECTION_STATES } from '../../contexts/SocketContext';
import './ConnectionStatus.css';

const ConnectionStatus = ({ minimal = false, showWhenConnected = false, context = 'global' }) => {
  const { connectionState, error, reconnectAttempt, reconnect } = useSocket();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const autoDismissTimer = useRef(null);

  useEffect(() => {
    // Clear any pending auto-dismiss
    if (autoDismissTimer.current) {
      clearTimeout(autoDismissTimer.current);
      autoDismissTimer.current = null;
    }

    // Show when not connected or reconnecting
    if (connectionState === CONNECTION_STATES.CONNECTED) {
      // Show briefly when connected, then hide
      if (showWhenConnected) {
        setVisible(true);
        const timer = setTimeout(() => setVisible(false), 3000);
        return () => clearTimeout(timer);
      } else {
        setVisible(false);
      }
      setDismissed(false);
    } else if (connectionState === CONNECTION_STATES.ERROR) {
      if (!dismissed) {
        setVisible(true);
        // In global context, auto-dismiss errors after 6 seconds
        // Webinar context keeps them visible until user action
        if (context === 'global') {
          autoDismissTimer.current = setTimeout(() => {
            setVisible(false);
            setDismissed(true);
          }, 6000);
        }
      }
    } else if (connectionState === CONNECTION_STATES.RECONNECTING ||
               connectionState === CONNECTION_STATES.CONNECTING) {
      // In global context, don't show connecting/reconnecting banners
      // (these are noisy for non-webinar pages)
      if (context === 'webinar' && !dismissed) {
        setVisible(true);
      }
    } else {
      setVisible(false);
    }

    return () => {
      if (autoDismissTimer.current) {
        clearTimeout(autoDismissTimer.current);
      }
    };
  }, [connectionState, showWhenConnected, dismissed, context]);

  const handleDismiss = () => {
    setDismissed(true);
    setVisible(false);
  };

  const handleRetry = () => {
    setDismissed(false);
    reconnect();
  };

  if (!visible) return null;

  const getStatusInfo = () => {
    switch (connectionState) {
      case CONNECTION_STATES.CONNECTED:
        return {
          icon: '✓',
          message: 'Connected',
          className: 'connected',
          showRetry: false,
        };
      case CONNECTION_STATES.CONNECTING:
        return {
          icon: '◌',
          message: 'Connecting...',
          className: 'connecting',
          showRetry: false,
        };
      case CONNECTION_STATES.RECONNECTING:
        return {
          icon: '↻',
          message: `Reconnecting... (attempt ${reconnectAttempt})`,
          className: 'reconnecting',
          showRetry: false,
        };
      case CONNECTION_STATES.ERROR:
        return {
          icon: '!',
          message: error || 'Connection error',
          className: 'error',
          showRetry: true,
        };
      case CONNECTION_STATES.DISCONNECTED:
        return {
          icon: '○',
          message: 'Disconnected',
          className: 'disconnected',
          showRetry: true,
        };
      default:
        return {
          icon: '?',
          message: 'Unknown status',
          className: 'unknown',
          showRetry: true,
        };
    }
  };

  const status = getStatusInfo();

  if (minimal) {
    return (
      <div className={`connection-status-minimal ${status.className}`}>
        <span className="status-dot" />
      </div>
    );
  }

  return (
    <div className={`connection-status ${status.className}`}>
      <div className="status-content">
        <span className="status-icon">{status.icon}</span>
        <span className="status-message">{status.message}</span>
      </div>
      <div className="status-actions">
        {status.showRetry && (
          <button className="retry-btn" onClick={handleRetry}>
            Retry
          </button>
        )}
        <button className="dismiss-btn" onClick={handleDismiss}>
          ×
        </button>
      </div>
    </div>
  );
};

export default ConnectionStatus;
